/**
 * ModelViewer Component
 *
 * A comprehensive 3D model viewer for medical imaging applications.
 * Provides interactive measurement tools, area calculations, and depth estimation.
 *
 * Features:
 * - 3D model loading and rendering (PLY format)
 * - Distance measurement between points
 * - Area and perimeter calculation for polygons
 * - Depth estimation for volume calculations
 * - Interactive controls and visual customization
 * - Data persistence for measurement points
 *
 */

import React, { PropsWithChildren, ReactNode, Suspense, useEffect, useRef } from "react";

import {
  CircularProgress,
  Stack,
} from "@mui/material";

import { Vector3 } from "three";

import { urlExpired } from "../../utils/url-expired";

import { Canvas } from "@react-three/fiber";
import { Html, OrbitControls } from "@react-three/drei";

import ControlPanel from "./control-panel";
import {
  PointLineGroup,
  BoundingGridBox
} from "./geometry";
import { ModelFromUrl } from "./utils";
import { shoeStringArea } from "./utils/area-formulas";
import {
  DepthEstimationStatus,
  ModelViewerContext,
} from "../../context/model-viewer-context";
import { Footer } from "./ui";

/**
 * Props for the ModelViewer component
 */
export interface ModelViewerProps {
  /** Unique identifier for the patient */
  patientId: string;
  /** URL to the 3D model file (PLY format) */
  modelUrl?: string;
  /** Component height (CSS value, default: "80vh") */
  height?: string;
  /** Component width (CSS value, default: "60vw") */
  width?: string;
  /** Enable depth estimation features for volume calculation */
  showDepthEstimation?: boolean;
  /** Header components */
  headerComponent?: ReactNode;
}

const ModelViewer: React.FC<PropsWithChildren<ModelViewerProps>> = ({
  modelUrl,
  patientId,
  showDepthEstimation = false,
  headerComponent,
}) => {
  // === Model Position and Camera State ===
  /** 3D position of the model in the scene */
  const [modelPosition, setModelPosition] = React.useState(
    new Vector3(0, 0, 0)
  );
  /** Camera target position for orbit controls */
  const [target, setTarget] = React.useState(new Vector3(0, 0, 0));

  // === Measurement State ===
  /** Distance between first two measurement points */
  const [distance, setDistance] = React.useState<number>(-1);
  /** Total perimeter of the measurement polygon */
  const [perimeter, setPerimeter] = React.useState<number>(-1);
  /** Angle between first two measurement points */
  const [angle, setAngle] = React.useState<number>(-1);
  /** Calculated area of the measurement polygon */
  const [area, setArea] = React.useState<number>(-1);
  /** Array of measurement points in 3D space */
  const [points, setPoints] = React.useState<Vector3[]>([]);

  // === UI State ===
  /** Loading state for model and calculations */
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  /** Color for measurement lines and points */
  const [color, setColor] = React.useState<string>("#2563eb");
  /** Error message to display to user */
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  // === Advanced Measurement State ===
  /** 3D grid points for depth estimation visualization */
  const [gridPoints3D, setGridPoints3D] = React.useState<Vector3[]>([]);
  /** Normal vector of the measurement plane */
  const [planeNormal, setPlaneNormal] = React.useState<Vector3>(
    new Vector3(0, 0, 1)
  );
  /** Status of depth estimation calculation */
  const [depthStatus, setDepthStatus] = React.useState<number>(0);
  /** Calculated average depth for volume estimation */
  const [depthEstimation, setDepthEstimation] = React.useState<number>(-1);

  // === Tool State ===
  /** Whether area measurement tool is active (vs distance tool) */
  const [showAreaTool, setShowAreaTool] = React.useState<boolean>(true);

  // === Refs ===
  /** Reference to orbit controls for camera manipulation */
  const orbitControlsRef = useRef<any>(null);
  /** Track last model URL to detect changes */
  const [lastUrl, setLastUrl] = React.useState<string>(modelUrl || "");

  /**
   * Handles user clicks on the 3D model to add measurement points
   * 
   * For distance tool: limits to 2 points maximum (replaces existing after 2)
   * For area tool: allows unlimited points for polygon creation
   *
   * @param event - Click event containing 3D point coordinates
   */
  const pointsChanged = (event: any) => {
    const point = event.point;

    // Distance tool: replace points after 2, Area tool: accumulate points
    const newPoints = points.length === 2 && !showAreaTool
      ? [point]
      : [...points, point];

    // Need at least 2 points for any calculations
    if (newPoints.length < 2) {
      setPoints(newPoints);
      setDistance(-1);
      return;
    }

    updateValues(newPoints);
  };

  /**
   * Updates all measurement calculations when points change
   * 
   * Calculates:
   * - Area and perimeter for polygons (3+ points)
   * - Distance and angle for point pairs
   * - 3D grid points for depth estimation visualization
   * - Plane normal vector for depth calculations
   *
   * @param newPoints - Updated array of measurement points
   */
  const updateValues = (newPoints: Vector3[]) => {
    const { area, selfIntersecting, perimeter, gridPoints3D, normal } =
      shoeStringArea(newPoints, showDepthEstimation);

    // Prevent invalid polygon creation
    if (selfIntersecting) {
      setErrorMessage("Cannot add self intersecting point");
      return;
    }

    // Update all measurement values
    updateAngle(newPoints);
    updateDistance(newPoints);
    setGridPoints3D(gridPoints3D);
    setArea(area);
    setPerimeter(perimeter);
    setPlaneNormal(normal);
    setPoints(newPoints);
    setErrorMessage(null);
  };

  /**
   * Calculates straight-line distance between first two points
   * Used for distance measurement tool
   *
   * @param points - Array of measurement points
   */
  const updateDistance = (points: Vector3[]) => {
    if (points.length < 2) {
      setDistance(-1);
      return;
    }

    setDistance(points[0].distanceTo(points[1]));
  };

  /**
   * Calculates angle between first two points from origin
   * Converts from radians to degrees for display
   *
   * @param points - Array of measurement points  
   */
  const updateAngle = (points: Vector3[]) => {
    if (points.length < 2) {
      setAngle(-1);
      return;
    }

    const angle = (points[0].angleTo(points[1]) * 180) / Math.PI;
    setAngle(angle);
  };

  /**
   * Switches from area tool to distance measurement tool
   * Only activates if area tool is currently active
   */
  const toggleMeasureTool = () => {
    if (!showAreaTool) {
      return;
    }

    setShowAreaTool(false);
    resetPoints();
  };

  /**
   * Switches from distance tool to area measurement tool
   * Only activates if distance tool is currently active
   */
  const toggleAreaTool = () => {
    if (showAreaTool) {
      return;
    }

    setShowAreaTool(true);
    resetPoints();
  };

  /**
   * Clears all measurement data and resets the viewer to initial state
   * Called when switching tools or when user requests reset
   */
  const resetPoints = () => {
    setPoints([]);
    setDistance(-1);
    setAngle(-1);
    setArea(-1);
    setPerimeter(-1);
    setGridPoints3D([]);
    setPlaneNormal(new Vector3(0, 0, 1));
    setDepthStatus(DepthEstimationStatus.NOT_STARTED);
    setDepthEstimation(-1);
  };

  /**
   * Removes the most recently added measurement point
   * Recalculates all measurements after point removal
   */
  const undoLastPoint = () => {
    if (points.length === 0) {
      return;
    }

    const newPoints = points.slice(0, points.length - 1);
    setPoints(newPoints);

    // Recalculate area with remaining points
    const { area } = shoeStringArea(newPoints, showDepthEstimation);
    setArea(area);

    // Update distance and angle measurements
    updateDistance(newPoints);
    updateAngle(newPoints);

    // Reset depth calculations
    setDepthEstimation(-1);
    setDepthStatus(DepthEstimationStatus.NOT_STARTED);
  };

  // Check if the model URL has expired (for AWS S3 signed URLs)
  const expired = urlExpired(modelUrl || "");

  /**
   * Resets the 3D camera to its initial position and target
   */
  const resetOrbitControls = () => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.reset();
    }
  };

  /**
   * Loads predefined measurement points (e.g., from saved data)
   * Switches to area tool if currently using distance tool
   *
   * @param points - Array of Vector3 points to load
   */
  const loadPoints = (points: Vector3[]) => {
    // Ensure area tool is active for loading multiple points
    if (!showAreaTool) {
      setShowAreaTool(true);
    }

    setPoints(points);
    updateDistance(points);
    updateAngle(points);

    const { area } = shoeStringArea(points, showDepthEstimation);
    setArea(area);
  };

  // Effect to handle model URL changes and reset measurements
  useEffect(() => {
    if (modelUrl === lastUrl) {
      return;
    }

    setLastUrl(modelUrl || "");
    resetPoints();
    console.log("Model URL changed, resetting points");
  }, [modelUrl, lastUrl]);

  return (
    <ModelViewerContext.Provider
      value={{
        points,
        setPoints,
        distance,
        setDistance,
        angle,
        setAngle,
        area,
        setArea,
        perimeter,
        setPerimeter,
        showAreaTool,
        setShowAreaTool,
        isLoading,
        setIsLoading,
        depthEstimation,
        setDepthEstimation,
        depthStatus,
        setDepthStatus,
      }}
    >
      <Stack
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#333333",
          position: "relative",
          borderRadius: "0.5rem",
          height: "100%",
          width: "100%",
        }}
      >
        {headerComponent &&
          <Stack
            direction="row"
            justifyContent="space-evenly"
            alignItems="center"
            spacing={1}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              padding: "12px",
              textAlign: "center",
              borderTopLeftRadius: "0.5rem",
              borderTopRightRadius: "0.5rem",
              zIndex: 100,
            }}
          >
            {headerComponent}
          </Stack >}
        {/* 3D Canvas Container */}
        <Canvas>
          {modelUrl && (
            <Suspense
              fallback={
                <Html position={[0, 0, 0]}>
                  <CircularProgress />
                </Html>
              }
            >
              {/* Loading indicator overlay */}
              {isLoading && (
                <Html position={[0, 0, 0]}>
                  <CircularProgress />
                </Html>
              )}

              {/* Main 3D model component */}
              <ModelFromUrl
                url={modelUrl}
                gridPoints3D={gridPoints3D}
                position={modelPosition}
                expired={expired}
                planeNormal={planeNormal}
                onClick={pointsChanged}
              />
            </Suspense>
          )}

          {/* Camera controls */}
          <OrbitControls target={target} ref={orbitControlsRef} />

          {/* Measurement visualization components */}
          <PointLineGroup
            points={points}
            visible={!showAreaTool || showAreaTool}
            stringLineColor={color}
          />
          <BoundingGridBox
            gridPoints3D={gridPoints3D}
            visible={!showAreaTool || showAreaTool}
          />

          {/* Optional plane visualization (currently commented out) */}
          {/* <PlaneFromPoints points={points} visible={!showAreaTool || showAreaTool} /> */}
        </Canvas>

        {/* Control Panel - Positioned as overlay on top-left */}
        <Stack
          sx={{
            position: "absolute",
            top: headerComponent ? "80px" : "16px",
            left: "16px",
            zIndex: 1000,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            borderRadius: "8px",
            padding: "8px",
          }}
        >
          <ControlPanel
            showAreaTool={showAreaTool}
            onAreaToolToggle={toggleAreaTool}
            onMeasureToolToggle={toggleMeasureTool}
            onCenterModel={resetOrbitControls}
            onReset={resetPoints}
            canReset={points.length > 0}
            canCalculateDepth={points.length > 2}
            onUndo={undoLastPoint}
            color={color}
            setColor={setColor}
            showDepthEstimation={showDepthEstimation}
            patientId={patientId}
            modelLoaded={modelUrl != undefined}
            points={points}
            area={area}
            onPointsUploaded={loadPoints}
          />
        </Stack>

        {/* Status Bar - Instructions and measurements at bottom */}
        <Footer
          showAreaTool={showAreaTool}
          area={area}
          perimeter={perimeter}
          distance={distance}
          angle={angle}
          showDepthEstimation={showDepthEstimation}
          depthEstimation={depthEstimation}
          errorMessage={errorMessage}
        />
      </Stack>
    </ModelViewerContext.Provider>
  );
};

export default ModelViewer;
